import { useCallback, useEffect, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";

import { Disclose, Headline, Loading, Refusal, Tooltip } from "@lopie/kernel-ui";

/**
 * What can leave here, and what has?
 *
 * ## The half of the picture nothing drew
 *
 * Wiring answers *where does each agent's data come from* in four columns —
 * connector, type, app, agent — and it is the whole inbound path. There was no
 * outbound equivalent, which meant the one category of write this product
 * calls the most dangerous had no screen at all. `egress.py` puts it plainly:
 * outbound is *"the one category of write the log cannot check against
 * anything it holds"*. Everything else the kernel records can be verified
 * against a record it still has. A text message cannot be recalled and cannot
 * be inspected; the only evidence it ever existed is the row written when it
 * was queued.
 *
 * ## Three columns, because the fourth is not the kernel's to claim
 *
 * `what leaves → how it leaves → who may send it`. The natural fourth column
 * is *which provider* — and the kernel does not know. `EgressSpec` carries a
 * channel and nothing else; which company turns `email` into an email is a
 * deployment fact held by the worker's environment, not by anything the
 * kernel governs.
 *
 * That gap is worth showing rather than papering over, because it is the
 * honest shape of the guarantee: **the kernel decides what may be sent and
 * has no idea what sends it.** A screen naming SendGrid would be reading a
 * Terraform variable and presenting it as governance.
 *
 * ## Queued is not delivered, and the log knows the difference
 *
 * A row here means the kernel accepted a send and wrote it down. Something
 * else holds the provider's credential and makes the call, and whether it
 * arrived is invisible from this side — as is a bounce four days later. Said
 * on the screen, because "12 sent" reads as twelve arrivals otherwise.
 */

const count = (n) => Number(n || 0).toLocaleString("en-US");

/** What a channel is called on screen, for somebody who does not read verbs. */
const PLAIN = {
  email: "Email",
  sms: "Text message",
  voice: "Phone call",
};

/** Rows to ask for per verb. One partition read each, so it is cheap. */
const ROWS = 200;

function Row({ row }) {
  const nobody = !row.senders.length;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr auto",
          sm: "minmax(0, 1fr) 6rem minmax(0, 1fr) 6rem",
        },
        gap: { xs: 0.5, sm: 2 },
        alignItems: "baseline",
        py: 1.5,
        borderTop: 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {PLAIN[row.channel] || row.channel}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "42ch", display: "block" }}>
          {row.detail}
        </Typography>
      </Box>

      <Tooltip title={`Sent by ${row.verbs.join(" and ")}`}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ cursor: "help", fontFamily: "ui-monospace, monospace" }}
        >
          {row.verbs.length}{" "}
          {row.verbs.length === 1 ? "verb" : "verbs"}
        </Typography>
      </Tooltip>

      <Box sx={{ minWidth: 0 }}>
        {nobody ? (
          <Typography variant="body2" sx={{ color: "kernel.attention", fontWeight: 600 }}>
            nobody
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
            {row.senders.map((s) => (
              <Chip key={s.app_type} size="small" variant="outlined"
                    label={s.label || s.app_type} />
            ))}
          </Stack>
        )}
      </Box>

      <Tooltip
        title={
          row.stopped
            ? "A floor — the read stopped before it finished."
            : "Rows the kernel wrote when it accepted a send. Not a delivery count."
        }
      >
        <Typography
          variant="body2"
          sx={{
            cursor: "help",
            fontVariantNumeric: "tabular-nums",
            textAlign: { sm: "right" },
          }}
        >
          {count(row.sent)}
          {row.stopped ? "+" : ""}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function Outbound({ get, onEffective }) {
  const [rows, setRows] = useState(null);
  const [unattended, setUnattended] = useState(0);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    try {
      // The vocabulary comes from the kernel, so a fourth channel added to
      // `egress.py` appears here with no change to this file.
      const [apps, feed] = await Promise.all([
        get("/apps"),
        get("/audit", { limit: 1 }),
      ]);
      // Channels, not verbs. `message.send` and `call.place` are two verbs
      // and one fact about risk — both reach a person on a telephone — and a
      // reader should be told that once rather than asked to notice it.
      const channels = feed.vocabulary?.channels || [];

      const answered = await Promise.all(
        channels.map(async (c) => {
          const verbs = c.verbs || [];
          const senders = (apps.apps || []).filter((a) => {
            const all = new Set([
              ...(a.actions || []), ...(a.service_actions || []),
            ]);
            return verbs.some((v) => all.has(v));
          });

          // One partition read per verb, summed into the channel.
          const pages = await Promise.all(
            verbs.map((v) =>
              get("/audit", { action: v, limit: ROWS, sort: "desc" })
                .catch(() => ({ items: [], stopped_early: false })),
            ),
          );
          const items = pages.flatMap((p) => p.items || []);
          return {
            channel: c.name,
            detail: c.detail,
            verbs,
            senders,
            sent: items.length,
            stopped: pages.some((p) => p.stopped_early),
            nobodyBehind: items.filter(
              (r) => !r.provenance?.on_behalf_of).length,
          };
        }),
      );
      // Busiest first. Alphabetical put the channel nothing can use in the
      // middle of the table; by volume it falls to the bottom where a zero
      // belongs and the reader gets a ranking rather than an index.
      answered.sort((a, b) => b.sent - a.sent);
      setRows(answered);
      setUnattended(answered.reduce((n, r) => n + r.nobodyBehind, 0));
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setRows(null);
    }
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!rows) return <Loading label="Asking what has left this kernel" />;

  const total = rows.reduce((n, r) => n + r.sent, 0);
  const unusable = rows.filter((r) => !r.senders.length);
  const allUnattended = total > 0 && unattended === total;

  return (
    <Stack spacing={3} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow={
          allUnattended
            ? "Left this kernel, with nobody behind any of it"
            : "Left this kernel"
        }
        figure={count(total)}
        tone={total ? "unattended" : undefined}
        caption={
          unattended
            // The share matters more than the total. Every one of these was
            // sent by something running on its own, and a person finding out
            // afterwards is the whole reason this screen exists.
            ? `${count(unattended)} of them with nobody behind it`
            : total === 1 ? "message" : "messages"
        }
        basis={
          "One read per verb, straight into that verb's own partition of the "
          + "log. A row means the kernel accepted a send and wrote it down — "
          + "not that anything arrived."
        }
        lede={
          <>
            This is the only kind of write that leaves the building. Everything
            else the kernel records can be checked against a record it still
            holds; a text message cannot be recalled and cannot be inspected,
            and the row written when it was queued is the only evidence it ever
            existed.
          </>
        }
      />

      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr auto",
              sm: "minmax(0, 1fr) 6rem minmax(0, 1fr) 6rem",
            },
            gap: { xs: 0.5, sm: 2 },
            pb: 0.5,
          }}
        >
          <Typography variant="overline" color="text.secondary">How it leaves</Typography>
          <Typography variant="overline" color="text.secondary">Verbs</Typography>
          <Typography variant="overline" color="text.secondary">Who may send it</Typography>
          <Typography variant="overline" color="text.secondary" sx={{ textAlign: { sm: "right" } }}>
            Gone
          </Typography>
        </Box>
        {rows.map((r) => (
          <Row key={r.channel} row={r} />
        ))}
      </Box>

      <Disclose label="What this does and does not say">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Gone means queued, not delivered.</strong> The kernel
            accepted the send and wrote it down; something else holds the
            provider&apos;s credential and makes the call. Whether it arrived
            is invisible from here, and so is a bounce four days later.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>The kernel does not know what sends these.</strong> It
            governs what may be sent and by which channel; which company turns{" "}
            <em>email</em> into an email is a deployment fact held by the
            worker, not by anything the kernel decides. A column naming a
            provider here would be reading a configuration variable and
            presenting it as governance.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>The log names a send, never a recipient.</strong> Each row
            carries an id like <code>send:01M2ET…</code>, so reading this
            screen discloses that something was sent and to nobody in
            particular — which is deliberate, and is why this page can be shown
            to somebody who may not see the records involved.
          </Typography>
          {unusable.length ? (
            <Typography variant="body2" sx={{ color: "kernel.attention" }}>
              {unusable.map((r) => PLAIN[r.channel] || r.channel).join(" and ")}{" "}
              {unusable.length === 1 ? "has" : "have"} no app permitted to
              use {unusable.length === 1 ? "it" : "them"}. The channel exists
              and nothing can reach it until a manifest declares the verb,
              which is a review rather than a setting.
            </Typography>
          ) : null}
        </Stack>
      </Disclose>
    </Stack>
  );
}
